import React from "react";
import { motion } from "framer-motion";
import { Hammer, ShieldCheck, Wrench, Sparkles, ArrowRight } from "lucide-react";

const STAGES = [
  {
    num: "01",
    icon: Hammer,
    title: "Demolition",
    tag: "Entry stage",
    desc: "Soft strip, partitions, floors, ceilings, kitchens and bathrooms — removed cleanly, waste carried away.",
  },
  {
    num: "02",
    icon: ShieldCheck,
    title: "Preparation",
    tag: "Site ready",
    desc: "Protection, dust management, service isolations, levelling and structural prep for follow-on trades.",
  },
  {
    num: "03",
    icon: Wrench,
    title: "Build",
    tag: "First & second fix",
    desc: "Partitions, M&E first-fix, carpentry, plastering and structural works delivered on programme.",
  },
  {
    num: "04",
    icon: Sparkles,
    title: "Finishing",
    tag: "Handover",
    desc: "Decoration, flooring, tiling, kitchens, bathrooms and snagging. Handed over spotless, signed-off.",
  },
];

export default function CompleteDelivery({ eyebrow = "Complete Project Delivery" }) {
  return (
    <section
      id="complete-delivery"
      data-testid="complete-delivery-section"
      className="bg-white text-[#2B2420] py-20 md:py-28"
    >
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
              <span className="brand-gold-divider" /> {eyebrow}
            </div>
            <h2
              className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl max-w-3xl"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Demolition. Preparation. <br /> Build. Finishing.
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-[#8A7F74]">
            One contractor, four stages, one programme — or join us at the
            stage you need. Small jobs and full refurbishments, both welcome.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 border-t border-l border-[#E5DDD0]">
          {STAGES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.num}
                data-testid={`delivery-stage-${s.num}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative border-r border-b border-[#E5DDD0] p-7 md:p-8 min-h-[260px] flex flex-col justify-between bg-white hover:bg-[#F5EFE5] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <span
                    className="font-black leading-none text-5xl md:text-6xl text-[#B8732C]"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {s.num}
                  </span>
                  <Icon className="w-6 h-6 text-[#C9A24C]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] mb-2 text-[#B8732C] font-bold">
                    {s.tag}
                  </div>
                  <h3
                    className="font-black tracking-tight text-2xl md:text-3xl leading-[1.05]"
                    style={{ fontFamily: "Cabinet Grotesk" }}
                  >
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#8A7F74]">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 md:mt-12 flex flex-col sm:flex-row sm:items-center gap-4">
          <a
            href="#quote"
            data-testid="delivery-cta"
            className="inline-flex items-center gap-2 bg-[#B8732C] hover:bg-[#9A5D1E] text-white px-6 py-4 text-sm font-bold uppercase tracking-[0.15em] transition-colors"
          >
            Plan your project <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-sm text-[#8A7F74]">
            Need only one stage? We also work alongside main contractors and PMs.
          </p>
        </div>
      </div>
    </section>
  );
}
